// src/components/Sidebar.jsx
import React, { useState, useCallback, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import {
  FiHome,
  FiBriefcase,
  FiUsers,
  FiSettings,
  FiBell,
  FiUser,
  FiLogOut,
  FiChevronDown,
  FiChevronRight,
  FiX,
} from 'react-icons/fi';
import gecimmoLogo2 from '../../image/gecimmoLogo2.png';
import gecimmoLogo from '../../image/gecimmoLogo.png';
import { useNavigate } from 'react-router-dom';
import api from '../../api'


const menuItems = [
  // { icon: FiHome, label: 'Dashboard', path: '/admin' },
  { icon: FiBriefcase, label: 'Organisations', path: '/admin/organisations' },
  { icon: FiUsers, label: 'Utilisateurs', path: '/admin/employees' },
  { icon: FiSettings, label: 'Paramétrage', path: '/admin/settings', hasSubMenu: true },
  { icon: FiBell, label: 'Notifications', path: '/admin/notifications' },
];



// Composant pour les éléments du sous-menu
function SubMenuItem({ to, label, onClick, isMobile }) {
  return (
    <li>
      <Link
        to={to}
        onClick={onClick}
        className={`block p-2 text-sm text-gray-200 hover:text-white hover:bg-black rounded transition-colors
          ${isMobile ? 'pl-10' : ''}`}
      >
        {label}
      </Link>
    </li>
  );
}

function NavItem({
  Icon,
  label,
  path,
  collapsed,
  active,
  hasSubMenu,
  isSubMenuOpen,
  onClick,
  onToggleSubMenu,
  isMobile
}) {
  return (
    <li className="group">
      {hasSubMenu ? (
        <div
          onClick={onToggleSubMenu}
          className={`flex items-center justify-between gap-4 p-3 rounded-xl cursor-pointer transition-colors
            ${active ? 'bg-white text-black' : 'text-gray-100 hover:bg-black hover:text-white'}
            ${collapsed || isMobile ? 'justify-center' : ''}`}
        >
          <div className="flex items-center gap-4">
            <Icon className="w-6 h-6" />
            {!collapsed && !isMobile && <span className="font-medium truncate">{label}</span>}
          </div>
          {!collapsed && !isMobile && (isSubMenuOpen ? <FiChevronDown /> : <FiChevronRight />)}
        </div>
      ) : (
        <NavLink
          to={path}
          onClick={onClick}
          className={({ isActive }) => `flex items-center gap-4 p-3 rounded-xl transition-colors
            ${isActive || active ? 'bg-white text-black' : 'text-gray-100 hover:bg-black hover:text-white'}
            ${collapsed || isMobile ? 'justify-center' : ''}`}
        >
          <Icon className="w-6 h-6" />
          {!collapsed && !isMobile && <span className="font-medium truncate">{label}</span>}
        </NavLink>
      )}

      {/* Sous-menu pour desktop */}
      {hasSubMenu && isSubMenuOpen && !collapsed && !isMobile && (
        <ul className="ml-10 mt-2 space-y-1">
          <SubMenuItem 
            to="/admin/settings/Parametresgeneraux" 
            label="Tableau Étapes" 
            isMobile={isMobile}
          />
          <SubMenuItem 
            to="/admin/settings/etapes" 
            label="Étapes" 
            isMobile={isMobile}
          />
          <SubMenuItem 
            to="/admin/settings/ParamFonctionnelsOrgas" 
            label="Organisation fonctionnel" 
            isMobile={isMobile}
          />
          <SubMenuItem 
            to="/admin/settings/modules" 
            label="modules" 
            isMobile={isMobile}
          />
          <SubMenuItem 
            to="/admin/settings/validations" 
            label="Validations" 
            isMobile={isMobile}
          />
        </ul>
      )}
    </li>
  );
}

export default function Sidebar({ onCollapseChange }) {
  const [collapsed, setCollapsed] = useState(false);
  const [activeLabel, setActiveLabel] = useState('Dashboard');
  const [openSubMenu, setOpenSubMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showMobileSubMenu, setShowMobileSubMenu] = useState(false);
  const navigate = useNavigate();

const handleLogout = async () => {
  try {
    await api.post('/logout',{ withCredentials: true }); // <- envoi du cookie

    // Supprimer le token côté frontend si stocké
    localStorage.removeItem('token'); 
    sessionStorage.clear();

    // Rediriger vers la page de login
    navigate('/');
  } catch (error) {
    console.error('Erreur lors de la déconnexion:', error);
  }
};

const secondaryItems = [
  { icon: FiUser, label: 'Profile', path: '/admin/profile' },
  { icon: FiLogOut, label: 'Logout', action: handleLogout }
];


  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      
      // En mode mobile, on force la sidebar à être "collapsed" (icônes seulement)
      if (mobile) {
        setCollapsed(true);
        onCollapseChange?.(true);
      }
    };

    window.addEventListener('resize', handleResize);
    // Initialiser l'état au chargement
    handleResize();
    
    return () => window.removeEventListener('resize', handleResize);
  }, [onCollapseChange]);

  const handleMouseEnter = () => {
    if (!isMobile) {
      setCollapsed(false);
      onCollapseChange?.(false);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setCollapsed(true);
      onCollapseChange?.(true);
    }
  };

  const handleItemClick = useCallback(label => {
    setActiveLabel(label);
    setOpenSubMenu(false);
    // Fermer le sous-menu mobile si on clique sur un autre élément
    if (showMobileSubMenu && label !== 'Paramétrage') {
      setShowMobileSubMenu(false);
    }
  }, [showMobileSubMenu]);

  const handleToggleSubMenu = useCallback(() => {
    if (isMobile) {
      // En mode mobile, afficher le sous-menu dans un overlay
      setShowMobileSubMenu(true);
    } else {
      // En mode desktop, toggle le sous-menu normal
      setOpenSubMenu(prev => !prev);
    }
    setActiveLabel('Paramétrage');
  }, [isMobile]);

  const handleCloseMobileSubMenu = useCallback(() => {
    setShowMobileSubMenu(false);
  }, []);

  return (
    <>
      <aside
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className={`fixed top-0 left-0 h-full bg-terrecuite text-white flex flex-col
          transition-all duration-300 shadow-lg z-40
          ${isMobile ? 'w-20' : (collapsed ? 'w-20' : 'w-64')}`}
      >
        <header className="p-4 border-b border-terrecuite">
          <div className={`flex ${collapsed || isMobile ? 'flex-col items-center space-y-2' : 'items-center justify-between'}`}>
            <img
              src={collapsed || isMobile ? gecimmoLogo2 : gecimmoLogo}
              alt="Logo Gecimmo"
              className="h-12 transition-transform duration-300 hover:scale-105"
            />
          </div>
        </header>

        <nav className="flex-1 p-4 overflow-y-auto">
          <ul className="space-y-1">
            {menuItems.map(item => (
              <NavItem
                key={item.label}
                Icon={item.icon}
                label={item.label}
                path={item.path}
                collapsed={collapsed}
                active={activeLabel === item.label}
                hasSubMenu={item.hasSubMenu}
                isSubMenuOpen={openSubMenu}
                onClick={() => handleItemClick(item.label)}
                onToggleSubMenu={handleToggleSubMenu}
                isMobile={isMobile}
              />
            ))}
          </ul>
        </nav>

<footer className="p-4 border-t border-terrecuite">
  <ul className="space-y-1">
    {secondaryItems.map(item => {
      if(item.label === 'Logout') {
        return (
          <li key={item.label}>
            <button
              onClick={handleLogout}
              className="flex items-center gap-4 p-3 rounded-xl text-gray-100 hover:bg-black hover:text-white w-full"
            >
              <item.icon className="w-6 h-6" />
              {!collapsed && !isMobile && <span>{item.label}</span>}
            </button>
          </li>
        );
      } else {
        return (
          <NavItem
            key={item.label}
            Icon={item.icon}
            label={item.label}
            path={item.path}
            collapsed={collapsed}
            active={activeLabel === item.label}
            onClick={() => handleItemClick(item.label)}
            isMobile={isMobile}
          />
        );
      }
    })}
  </ul>
</footer>

      </aside>

      {/* Overlay pour le sous-menu en mode mobile */}
      {showMobileSubMenu && isMobile && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50" onClick={handleCloseMobileSubMenu}>
          <div 
            className="fixed top-0 left-20 h-full w-64 bg-terrecuite text-white shadow-lg overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 border-b border-terrecuite flex items-center justify-between">
              <h2 className="text-xl font-semibold">Paramétrage</h2>
              <button 
                onClick={handleCloseMobileSubMenu}
                className="p-1 rounded-full hover:bg-black"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
            
            <nav className="p-4">
              <ul className="space-y-2">
                <SubMenuItem 
                  to="/admin/settings/Parametresgeneraux" 
                  label="Tableau Étapes" 
                  onClick={handleCloseMobileSubMenu}
                  isMobile={isMobile}
                />
                <SubMenuItem 
                  to="/admin/settings/etapes" 
                  label="Étapes" 
                  onClick={handleCloseMobileSubMenu}
                  isMobile={isMobile}
                />
                <SubMenuItem 
                  to="/admin/settings/ParamFonctionnelsOrgas" 
                  label="Organisation fonctionnel" 
                  onClick={handleCloseMobileSubMenu}
                  isMobile={isMobile}
                />
                <SubMenuItem 
                  to="/admin/settings/modules" 
                  label="modules" 
                  onClick={handleCloseMobileSubMenu}
                  isMobile={isMobile}
                />
                <SubMenuItem 
                  to="/admin/settings/validations" 
                  label="Validations" 
                  onClick={handleCloseMobileSubMenu}
                  isMobile={isMobile}
                />
              </ul>
            </nav>
          </div>
        </div>
      )}
    </>
  );
}